import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ResponseModel } from '../../models/response.model';
import { appEnv } from '../../../app.config';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ShipmentService {

  constructor(private http: HttpClient) { }

  public getAll (filter: {start_date: string, end_date: string, shift_type_id?: number | null} = {start_date: '', end_date: ''}): Observable<ResponseModel> {
    let url = `${appEnv.api}consignment/all?start_date=${filter.start_date}&end_date=${filter.end_date}`;
    if (filter.shift_type_id) {
      url += `&shift_type_id=${filter.shift_type_id}`;
    }
    return this.http.get<ResponseModel>(url);
  }
  
  public updateStopStatus (id: number = 0, statusId: number = 0): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/stop-status?id=${id}`, {status_id: statusId});
  }

  public updateServiceType (id: number = 0, serviceTypeId: number = 0): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/service-type?id=${id}`, {service_type_id: serviceTypeId});
  }

  public deleteShift (id: number = 0): Observable<ResponseModel> {
    return this.http.delete<ResponseModel>(`${appEnv.api}consignment/shift/record?id=${id}`);
  }

  public bulkDeleteConsignment (ids: number[] = []): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/bulk-delete`, {ids: ids});
  }

  public uploadPOD (id: number = 0, data: any): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/upload-pod?id=${id}`, data);
  }

  public bulkUpdateConsignmentStatus (ids: number[] = [], statusId: number = 0, dateTime: string = ''): Observable<ResponseModel> {
    if (!dateTime) {
      dateTime = moment().format('YYYY-MM-DD HH:mm:ss');
    }
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/bulk-update-status`, {ids: ids, status_id: statusId, datetime: dateTime});
  }

  public bulkUpdateServiceType (ids: number[] = [], serviceId: number = 0): Observable<ResponseModel> {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/bulk-update-service-type`, {ids: ids, service_id: serviceId});
  }

  public bulkRemoveConsignmentShift (consignmentIds: number[] = []) {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/bulk-remove-consignment-shift`, {ids: consignmentIds});
  }

  public updatePOD (id: number = 0, podArray: any[] = []) {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/update-pod?id=${id}`, {pod: podArray});
  }

  public getPODB64 (imageUrl: string = '') {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/pod-b64`, {file: imageUrl});
  }

  public assignContractor (data: {employee_id: number,role_id:number,stop_id:number,vehicle_type_id:number}) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/assign-contractor`, data);
  }

  removeContractor(id: number) {
    return this.http.delete<ResponseModel>(`${appEnv.api}consignment/remove-contractor/${id}`);
  }

  createShiftWithMergedConsignment (data: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/merged-consignment`, {items: data});
  }

  updateShipmentItems (id: number = 0, post: any[] = [], references: any) {
    return this.http.put<ResponseModel>(`${appEnv.api}consignment/bulk-shipment-items?id=${id}`, {items: post, references: {
      customerReference: references.customerReference,
      additionalReference: references.additionalReference,
    }});
  }

  public updatePickupDetails(id: number = 0, payload: any = {}) {
    return this.http.put<ResponseModel>(`${appEnv.api}consignment/update-pickup-details?id=${id}`, payload);
  }

  public updateDropoffDetails(id: number = 0, payload: any = {}) {
    return this.http.put<ResponseModel>(`${appEnv.api}consignment/update-dropoff-details?id=${id}`, payload);
  }

  public publishShift (data: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/save-publish`, data);
  }

  public calculateConsignmentRates (id: number = 0) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/calculate-rates`, {id: id});
  }

  /** Live "auto-calculate" preview of Total Cost/Total Pay from pickup/dropoff state, before the shipment exists — no consignment ID or assigned contractor required.
   * item_zone (1-8): prices off route_rates' Item Rate zone columns instead of the lane's normal base rate. */
  public previewRate (data: {client_id?: number, service_type_id: number, vehicle_type_id?: number | null, pickup_state_id: number, dropoff_state_id?: number | null, item_zone?: number | null}) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/preview-rate`, data);
  }

  public bulkAuthorisation (payloads: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/bulk-authorise`, payloads);
  }

  public getContractorRates (ids: number[]) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/contractor-rates`,{ids: ids});
  }

  public adjustContractorRateAmount (data: any) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/adjust-rate-amount`, data);
  }

  /** Live "auto-calculate" preview — recalculates the line items from an edited raw rate field, without persisting anything. */
  public previewContractorRateBreakdown (contractorId: number = 0, side: 'charge' | 'pay' = 'charge', rate: any = {}) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/preview-contractor-rate-breakdown`, {contractor_id: contractorId, side, rate});
  }

  /** Changes the charge and/or pay rate scope independently — pass only the side(s) being changed. */
  public updateContractorRateScope (assigneeId: number = 0, data: {charge_paid_type_id?: number, pay_paid_type_id?: number} = {}) {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/assigned-contractor/rate-scope`, {id: assigneeId, ...data});
  }

  public getShiftWithContractor (ids: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/shift/with-contractor`, {ids: ids});
  }

  public getShipmentShifts (ids: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/shift/get-data`, {ids: ids});
  }

  public getPickupDropoff (ids: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/pickup-dropoff`, {ids: ids});
  }

  public getOtherDetails (ids: number[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/other-details`, {ids: ids});
  }

  public getAvailableMachships () {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/get-available-machship`);
  }

  public updatePriceBreakdown (payloads: any) {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/price-breakdown`, payloads);
  }

  /** Full change history for a Job's Price Breakdown rate row (charge and/or pay side), newest first. */
  public getPriceBreakdownLogs (thChargeId?: number, thPayId?: number) {
    const params: Record<string, string> = {};
    if (thChargeId) params['th_charge_id'] = String(thChargeId);
    if (thPayId) params['th_pay_id'] = String(thPayId);
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/price-breakdown-logs`, { params });
  }

  public reSyncMachshipRates (shipmentId: number = 0) {
    return this.http.patch<ResponseModel>(`${appEnv.api}consignment/resync-machship-rates`, {id: shipmentId});
  }

  /** Backend sweep: sets any empty charge/pay rate fields on consignments and jobs to 0. Fired once per app load. */
  public fixEmptyRates (): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/fix-empty-rates`, {});
  }

  updateShipmentTailgate (shipmentId: number = 0, tailgate: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}consignment/tailgate`, {shipment_id: shipmentId, tailgate: tailgate});
  }

  updateShipmentJockey (shipmentId: number = 0, jockey: number = 0): Observable<ResponseModel> {
    return this.http.put<ResponseModel>(`${appEnv.api}consignment/jockey`, {shipment_id: shipmentId, jockey: jockey});
  }

  /** Full change history for a shipment (Consignment/Job) record, newest first. */
  getShipmentLogs (shipmentId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/logs`, { params: { shipment_id: String(shipmentId) } });
  }

  generateLabels (shipmentId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/generate-labels?id=${shipmentId}`);
  }

  generateManifestLabels (shipmentId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/generate-manifest?id=${shipmentId}`);
  }

  generateConsignmentNotes (shipmentId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/generate-consignment-notes?id=${shipmentId}`);
  }

  downloadAllAttachments (shipmentId: number) {
    return this.http.get<ResponseModel>(`${appEnv.api}consignment/download-attachments?id=${shipmentId}`);
  }

  bulkSendEmailAttachments (shipmentId: number = 0, emails: string[] = []) {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/send-attachments-via-emails`, {
      shipmentId,
      emails
    });
  }

  sendDriverManifestInfo (shipmentId: number = 0, email: string = '') {
    return this.http.post<ResponseModel>(`${appEnv.api}consignment/send-driver-email-manifest-info`, {
      shipmentId,
      email
    });
  }

}